import { IExpression } from "../Models/IExpression";
import { IExpressionEvaluator, IHasExpressionMetadata } from "../Models/IExpressionEvaluator";
import { IExpressionEvaluatorContext } from "../Models/IExpressionEvaluatorContext";
import { IExpressionEvaluatorRoot } from "../Models/IExpressionEvaluatorRoot";
import { IExpressionEvaluatorWithAssign } from "../Models/IExpressionEvaluatorWithAssign";
import { VarExpression, VarExpressionType } from "../Models/VarExpression";
import { CollectionExpressionEvaluator } from "./Evaluators/CollectionExpressionEvaluator";
import { CollectionConcatExpressionEvaluator } from "./Evaluators/Collections/CollectionConcatExpressionEvaluator";
import { CollectionFilterExpressionEvaluator } from "./Evaluators/Collections/CollectionFilterExpressionEvaluator";
import { CollectionMapExpressionEvaluator } from "./Evaluators/Collections/CollectionMapExpressionEvaluator";
import { CollectionSortExpressionEvaluator } from "./Evaluators/Collections/CollectionSortExpressionEvaluator";
import { EqExpressionEvaluator } from "./Evaluators/Comparators/EqExpressionEvaluator";
import { IsEmptyExpressionEvaluator } from "./Evaluators/Comparators/IsEmptyExpressionEvaluator";
import { IsNotEmptyExpressionEvaluator } from "./Evaluators/Comparators/IsNotEmptyExpressionEvaluator";
import { NotEqExpressionEvaluator } from "./Evaluators/Comparators/NotEqExpressionEvaluator";
import { ConstExpressionEvaluator } from "./Evaluators/ConstExpressionEvaluator";
import { ConversationAddMessageExpressionEvaluator } from "./Evaluators/Conversations/ConversationAddMessageExpressionEvaluator";
import { ConversationCreateExpressionEvaluator } from "./Evaluators/Conversations/ConversationCreateExpressionEvaluator";
import { ConversationGetByIdExpressionEvaluator } from "./Evaluators/Conversations/ConversationGetByIdExpressionEvaluator";
import { ObjectMapExpressionEvaluator } from "./Evaluators/Conversion/ObjectMapExpressionEvaluator";
import { ToGuidExpressionEvaluator } from "./Evaluators/Conversion/ToGuidExpressionEvaluator";
import { DomainServiceEnumItemsExpressionEvaluator } from "./Evaluators/DomainServices/DomainServiceEnumItemsExpressionEvaluator";
import { DomainServiceOperationCallExpressionEvaluator } from "./Evaluators/DomainServices/DomainServiceOperationCallExpressionEvaluator";
import { AndExpressionEvaluator } from "./Evaluators/Logical/AndExpressionEvaluator";
import { AndParallelExpressionEvaluator } from "./Evaluators/Logical/AndParallelExpressionEvaluator";
import { OrExpressionEvaluator } from "./Evaluators/Logical/OrExpressionEvaluator";
import { ObjectExpressionEvaluator } from "./Evaluators/ObjectExpressionEvaluator";
import { StringConcatExpressionEvaluator } from "./Evaluators/Strings/StringConcatExpressionEvaluator";
import { StringJoinExpressionEvaluator } from "./Evaluators/Strings/StringJoinExpressionEvaluator";
import { CloseDialogExpressionEvaluator } from "./Evaluators/UserInterface/CloseDialogExpressionEvaluator";
import { NavigateToExpressionEvaluator } from "./Evaluators/UserInterface/NavigateToExpressionEvaluator";
import { NotifyExpressionEvaluator } from "./Evaluators/UserInterface/NotifyExpressionEvaluator";
import { RefreshExpressionEvaluator } from "./Evaluators/UserInterface/RefreshExpressionEvaluator";
import { ValidateExpressionEvaluator } from "./Evaluators/UserInterface/ValidateExpressionEvaluator";
import { VarAssignExpressionEvaluator } from "./Evaluators/VarAssignExpressionEvaluator";
import { VarExpressionEvaluator } from "./Evaluators/VarExpressionEvaluator";
import { VarRemoveExpressionEvaluator } from "./Evaluators/VarRemoveExpressionEvaluator";
import { ViewModelLoadAssociationExpressionEvaluator } from "./Evaluators/ViewModels/ViewModelLoadAssociationExpressionEvaluator";
import { WhenExpressionEvaluator } from "./Evaluators/WhenExpressionEvaluator";

export class CompositeExpressionEvaluator implements IExpressionEvaluatorRoot<IExpression> {
  private evaluators: Map<string, IExpressionEvaluator<any>> = new Map();
  private evaluatorsWithAssign: Map<string, IExpressionEvaluatorWithAssign<any>> = new Map();
  private evaluatorsWithMetadata: Map<string, IExpressionEvaluator<any> & IHasExpressionMetadata> = new Map();
  private referencedVariablesCache: WeakMap<IExpression, VarExpression[]> = new WeakMap();

  constructor(evaluators?: IExpressionEvaluator<any>[]) {
    if (evaluators) {
      for (const evaluator of evaluators) {
        this.add(evaluator);
      }
    }
  }

  collectReferencedVariables(expression: IExpression): VarExpression[] {
    if (!expression) {
      return [];
    }

    const cached = this.referencedVariablesCache.get(expression);
    if (cached) {
      return cached;
    }

    const referencedVariables: VarExpression[] = [];
    this.collectReferencedVariablesInternal(expression, referencedVariables);
    this.referencedVariablesCache.set(expression, referencedVariables);
    return referencedVariables;
  }

  private collectReferencedVariablesInternal(expression: IExpression, referencedVariables: VarExpression[]): void {
    if (!expression) {
      return;
    }

    if (expression.Type === VarExpressionType) {
      referencedVariables.push(expression as VarExpression);
      return;
    }

    const evaluator = this.evaluatorsWithMetadata.get(expression.Type);
    if (evaluator) {
      for (let propertyName of evaluator.expressionProperties) {
        const childExpression = (expression as any)[propertyName];
        if (Array.isArray(childExpression)) {
          // Handle IExpression[]
          for (const childExpressionItem of childExpression) {
            this.collectReferencedVariablesInternal(childExpressionItem, referencedVariables);
          }
        } else if (childExpression && typeof childExpression === "object" && !childExpression.Type) {
          // Handle Record<string, IExpression> (e.g., ObjectExpression.Properties)
          for (const key in childExpression) {
            if (childExpression.hasOwnProperty(key)) {
              this.collectReferencedVariablesInternal(childExpression[key], referencedVariables);
            }
          }
        } else {
          // Handle single IExpression
          this.collectReferencedVariablesInternal(childExpression, referencedVariables);
        }
      }
    }
  }

  add(evaluator: IExpressionEvaluator<any>): void {
    this.evaluators.set(evaluator.type, evaluator);
    if ((evaluator as IExpressionEvaluatorWithAssign<any>).assign) {
      this.evaluatorsWithAssign.set(evaluator.type, evaluator as IExpressionEvaluatorWithAssign<any>);
    }
    if ((evaluator as IExpressionEvaluator<any> & IHasExpressionMetadata).expressionProperties) {
      this.evaluatorsWithMetadata.set(evaluator.type, evaluator as IExpressionEvaluator<any> & IHasExpressionMetadata);
    }
  }

  get type(): string {
    return "";
  }

  evaluate(expression: IExpression, context: IExpressionEvaluatorContext): Promise<any> {
    const evaluator = this.evaluators.get(expression.Type);
    if (evaluator) {
      const result = evaluator.evaluate(expression, context);
      return result instanceof Promise ? result : Promise.resolve(result);
    }
    return Promise.reject(new Error(`No evaluator found for expression type: ${expression.Type}`));
  }

  assign(expression: IExpression, context: IExpressionEvaluatorContext, value: any, path?: string[]): void {
    const evaluator = this.evaluatorsWithAssign.get(expression.Type);
    if (evaluator) {
      evaluator.assign(expression, context, value, path);
      return;
    }
    throw new Error(`No evaluator found for expression type: ${expression.Type}`);
  }

  tryAssign(expression: IExpression, context: IExpressionEvaluatorContext, value: any, path?: string[]): boolean {
    const evaluator = this.evaluatorsWithAssign.get(expression.Type);
    if (evaluator) {
      return evaluator.tryAssign(expression, context, value, path);
    }
    return false;
  }
}

export const GlobalCompositeExpressionEvaluator = new CompositeExpressionEvaluator([
  new ConstExpressionEvaluator(),
  new VarExpressionEvaluator(),
  new VarAssignExpressionEvaluator(),
  new VarRemoveExpressionEvaluator(),

  new EqExpressionEvaluator(),
  new NotEqExpressionEvaluator(),
  new IsEmptyExpressionEvaluator(),
  new IsNotEmptyExpressionEvaluator(),

  new OrExpressionEvaluator(),
  new AndExpressionEvaluator(),
  new AndParallelExpressionEvaluator(),

  new ObjectExpressionEvaluator(),
  new CollectionExpressionEvaluator(),

  new WhenExpressionEvaluator(),

  new StringConcatExpressionEvaluator(),
  new StringJoinExpressionEvaluator(),

  new ObjectMapExpressionEvaluator(),
  new ToGuidExpressionEvaluator(),

  new CollectionConcatExpressionEvaluator(),
  new CollectionFilterExpressionEvaluator(),
  new CollectionMapExpressionEvaluator(),
  new CollectionSortExpressionEvaluator(),

  new DomainServiceOperationCallExpressionEvaluator(),
  new DomainServiceEnumItemsExpressionEvaluator(),

  new ConversationGetByIdExpressionEvaluator(),
  new ConversationCreateExpressionEvaluator(),
  new ConversationAddMessageExpressionEvaluator(),

  new ViewModelLoadAssociationExpressionEvaluator(),

  new ValidateExpressionEvaluator(),
  new RefreshExpressionEvaluator(),
  new CloseDialogExpressionEvaluator(),
  new NavigateToExpressionEvaluator(),
  new NotifyExpressionEvaluator(),
]);
